import { Link } from "react-router-dom";
import styled from "styled-components";
import dinteroIcon from "../../assets/logos/dintero-icon.svg";
import {
    colors,
    distances,
    globalMobileBreakpoint,
    palette,
    zIndex,
} from "../../styles/constants";
import { paintInAnimation } from "../appPaintIn";
import { Button } from "../Buttons";
import View from "../View";

type WrapperProps = {
    isInspecting?: boolean;
    isTest?: boolean;
    isPaymentsNotEnabled?: boolean;
    isBackdropActive?: boolean;
};

const ignoredProps = [
    "isInspecting",
    "isTest",
    "isPaymentsNotEnabled",
    "isBackdropActive",
];

export const Wrapper = styled.header.withConfig<WrapperProps>({
    shouldForwardProp: (props) => !ignoredProps.includes(props),
})`
    position: fixed;
    inset-inline: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;

    margin-left: var(--navbar-width);
    width: calc(100% - var(--navbar-width));

    @media (max-width: ${globalMobileBreakpoint}px) {
        margin-left: 0;
        width: 100%;
    }

    max-height: var(--topbar-height);
    min-height: var(--topbar-height);
    z-index: ${zIndex.top - 1};
    top: 0;
    transition: all 0.15s;
    color: ${colors.text};
    /* Grows and shrinks with the test mode strip, see EnvBar */
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
    /* Left pane, test badge, then right pane paint in on the app's first load */
    &[data-paint="true"] .main-toolbar > * {
        ${paintInAnimation}
        &:nth-child(2) {
            --paint-index: 1;
        }
        &:nth-child(3) {
            --paint-index: 2;
        }
    }
    .main-toolbar {
        background: #ffffffee;
        border-bottom: 1px solid ${colors.borderDark};
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        width: 100%;
        padding-inline: 16px;
        padding-block: 12px;
        max-height: var(--topbar-main-height);
        min-height: var(--topbar-main-height);
        transition: all 0.15s;
        color: ${colors.text};
        backdrop-filter: blur(10px);

        @media (min-width: ${globalMobileBreakpoint + 1}px) {
            ${(props) =>
                props.isInspecting &&
                `
                background: ${palette.primary[50]}ee;
                border-bottom: 1px solid ${palette.primary[200]}ee;
            `}

            ${(props) =>
                props.isTest &&
                `
                background: ${palette.warning[50]}ee;
                border-bottom: 1px solid ${palette.warning[200]}ee;
            `}
        }
        @media (max-width: ${globalMobileBreakpoint}px) {
            padding-inline: ${distances.small};
        }
    }
`;

export const HiddenInMobile = styled.div`
    display: contents;
    @media (max-width: ${globalMobileBreakpoint}px) {
        display: none;
    }
`;

export const HiddenInDesktop = styled.div`
    display: contents;
    @media (min-width: ${globalMobileBreakpoint + 1}px) {
        display: none;
    }
`;

export const LeftPane = styled(View).attrs({ gap: "16px" })`
    transition: all 200ms;
    @media (max-width: ${globalMobileBreakpoint}px) {
        gap: ${distances.small};
    }
`;

export const RightPane = styled(View).attrs({ gap: "24px" })`
    @media (max-width: ${globalMobileBreakpoint}px) {
        gap: ${distances.small};
    }
`;

export const DinteroIcon = styled.div`
    background: url("${dinteroIcon}") no-repeat center;
    background-size: contain;
    width: 32px;
    height: 32px;
    cursor: pointer;
`;

export const TestModeBadge = styled.div`
    display: flex;
    padding: 2px 10px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    border-radius: 44px;
    background: ${palette.warning[500]};
    color: white;
    text-transform: uppercase;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
`;

export const BottomTooltip = styled.div.withConfig<{
    content: string;
}>({
    shouldForwardProp: (prop) => !["content"].includes(prop),
})`
    position: relative;
    display: inline-block;
    cursor: pointer;

    &::before {
        content: '${(props) => props.content}';
        position: absolute;
        background: ${palette.neutral[900]};
        color: white;
        border-radius: 3px;
        padding: 4px 8px;
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        z-index: 10;
        bottom: -100%;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s cubic-bezier(0.39, 0.575, 0.565, 1);
    }

    &::after {
        content: '';
        position: absolute;
        height: 10px;
        width: 10px;
        background: ${palette.neutral[900]};
        clip-path: polygon(0 0, 100% 0, 0 100%);
        z-index: 10;
        bottom: -12px;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s cubic-bezier(0.39, 0.575, 0.565, 1);
    }

    &:hover::before {
        opacity: 1;
    }

    &:hover::after {
        opacity: 1;
    }

    &[data-hidden='true'] {
        &:hover::before {
            opacity: 0;
        }

        &:hover::after {
            opacity: 0;
        }
    }
`;

export const AppLink = styled(Link)
    .withConfig<{ disabled?: boolean }>({
        shouldForwardProp: (prop) => !["disabled"].includes(prop),
    })
    .attrs({
        className: "app-link",
    })`
    color: ${palette.primary[500]};
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    width: 100%;
    transition: all 0.15s;
    outline: 4px solid transparent;
    &:hover {
        background: ${palette.primary[50]};
    }
    &:focus-visible {
        outline-color: ${palette.primary[100]};
        outline-offset: 6px;
    }
    ${(props) =>
        props.disabled &&
        `
        pointer-events: none;
        opacity: 0.5;
    `}
`;

export const AppLinkButton = styled(Button).attrs({
    className: "app-link-button",
})`
    color: ${palette.primary[500]};
    background: transparent;
    border: 1px solid transparent;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    width: 100%;
    transition: all 0.15s;
    outline: 4px solid transparent;
    &:focus-visible {
        outline-color: ${palette.primary[100]};
        outline-offset: 6px;
    }
    &:hover {
        background: ${palette.primary[50]};
        border: 1px solid ${palette.primary[50]};
    }
`;

export const Divider = styled.div`
    width: calc(100% + 16px);
    height: 1px;
    background: ${colors.borderDark};
    margin-block: 8px;
    margin-inline: -8px;
`;

export const EnvBar = styled(View).withConfig({
    shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})<WrapperProps>`
    background: ${colors.backgroundAlt};
    border-bottom: 1px solid ${colors.borderDark};
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding-inline: 16px;
    padding-block: 8px;
    max-height: var(--topbar-indicator-height);
    min-height: var(--topbar-indiactor-height);
    gap: ${distances.small};
    transition:
        background 0.15s,
        border-color 0.15s,
        max-height 0.15s,
        padding-block 0.15s,
        opacity 0.15s;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    ${(props) =>
        props.isPaymentsNotEnabled &&
        `
        background: ${palette.neutral[50]};
        border-bottom: 1px solid ${palette.neutral[200]};
        padding-inline: 0;
    `}

    ${(props) =>
        props.isInspecting &&
        `
        background: ${palette.primary[50]};
        border-bottom: 1px solid ${palette.primary[200]};
    `}

    ${(props) =>
        props.isTest &&
        `
        background: ${palette.warning[50]};
        border-bottom: 1px solid ${palette.warning[200]};
    `}


    /* Collapsed rather than display: none, so it grows and shrinks with the
       top bar when toggling test mode */
    ${(props) =>
        props.isTest || props.isInspecting || props.isPaymentsNotEnabled
            ? ``
            : `
        max-height: 0;
        padding-block: 0;
        border-bottom-width: 0;
        opacity: 0;
        overflow: hidden;`}
`;
